import { useEffect, useState } from 'react';
import {
  Heart, Sparkles, Flame, Quote, BookHeart, History, Loader2, Lightbulb,
} from 'lucide-react';
import { nexaApi, photoUrl } from './api';
import { NexaFilesSection } from './NexaFiles';

const EMOTION_META = {
  happiness: { label: 'Happiness', color: '#38BDF8' },
  sadness: { label: 'Sadness', color: '#818CF8' },
  anger: { label: 'Anger', color: '#EF4444' },
  frustration: { label: 'Frustration', color: '#F59E0B' },
  anxiety: { label: 'Anxiety', color: '#FB923C' },
  excitement: { label: 'Excitement', color: '#A78BFA' },
  curiosity: { label: 'Curiosity', color: '#2DD4BF' },
  loneliness: { label: 'Loneliness', color: '#64748B' },
  affection: { label: 'Affection', color: '#FB7185' },
  energy: { label: 'Energy', color: '#4ADE80' },
  irritation: { label: 'Irritation', color: '#F87171' },
};

const REL_META = {
  trust: 'Trust',
  closeness: 'Closeness',
  respect: 'Respect',
  comfort: 'Comfort',
  attachment: 'Attachment',
  affection: 'Affection',
  curiosity_about_lev: 'Curiosity about Lev',
  frustration: 'Frustration',
  protectiveness: 'Protectiveness',
  openness: 'Openness',
};

const KIND_COLORS = {
  emotion: 'text-rose-300 border-rose-400/25 bg-rose-400/10',
  relationship: 'text-emerald-300 border-emerald-400/25 bg-emerald-400/10',
  trait: 'text-sky-300 border-sky-400/25 bg-sky-400/10',
  opinion: 'text-violet-300 border-violet-400/25 bg-violet-400/10',
  experience: 'text-amber-300 border-amber-400/25 bg-amber-400/10',
  initiative: 'text-rose-300 border-rose-400/25 bg-rose-400/10',
  thought: 'text-cyan-300 border-cyan-400/25 bg-cyan-400/10',
  curiosity: 'text-teal-300 border-teal-400/25 bg-teal-400/10',
  entity: 'text-lime-300 border-lime-400/25 bg-lime-400/10',
  interest: 'text-indigo-300 border-indigo-400/25 bg-indigo-400/10',
  self_memory: 'text-fuchsia-300 border-fuchsia-400/25 bg-fuchsia-400/10',
  reflection: 'text-sky-300 border-sky-400/25 bg-sky-400/10',
  date: 'text-yellow-300 border-yellow-400/25 bg-yellow-400/10',
  identity: 'text-white/60 border-white/15 bg-white/5',
};

const fmtDate = (ts) => {
  try {
    return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      + ' · ' + new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const Bar = ({ label, value, baseline, color, note, testId }) => (
  <div data-testid={testId}>
    <div className="flex items-baseline justify-between mb-1">
      <span className="text-[12px] text-white/70">{label}</span>
      <span className="text-[11px] font-mono text-white/40">{Math.round(value)}</span>
    </div>
    <div className="relative h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
      <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
        style={{ width: `${value}%`, background: color }} />
      {baseline != null && (
        <div className="absolute top-[-2px] bottom-[-2px] w-[2px] bg-white/35"
          style={{ left: `${baseline}%` }} title={`usual: ${baseline}`} />
      )}
    </div>
    {note && <p className="text-[10.5px] text-white/30 mt-1 leading-snug">{note}</p>}
  </div>
);

const Section = ({ icon: Icon, title, accent, children, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className={`w-4 h-4 ${accent}`} strokeWidth={2} />
      <h3 className="text-[11px] font-mono tracking-[0.2em] text-white/50 uppercase">{title}</h3>
    </div>
    {children}
  </div>
);

export const NexaMindView = () => {
  const [state, setState] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [selfMemories, setSelfMemories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      nexaApi.get('/self'),
      nexaApi.get('/self/experiences'),
      nexaApi.get('/self/timeline'),
      nexaApi.get('/self/memories'),
    ]).then(([a, b, c, d]) => {
      setState(a.data);
      setExperiences(b.data.items || []);
      setTimeline(c.data.items || []);
      setSelfMemories(d.data.items || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center pt-20" data-testid="nexa-mind-loading">
        <Loader2 className="w-6 h-6 animate-spin text-white/30" />
      </div>
    );
  }
  if (!state?.id) {
    return <p className="text-sm text-white/40 pt-8 text-center">Her inner state isn&apos;t initialized yet.</p>;
  }

  const emotions = state.emotions || {};
  const dominant = Object.entries(emotions)
    .map(([k, e]) => ({ k, dev: (e.value - e.baseline) }))
    .sort((a, b) => Math.abs(b.dev) - Math.abs(a.dev))[0];

  return (
    <div className="space-y-4 pb-8" data-testid="nexa-mind-view">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="nexa-mind-identity">
        <div className="flex items-center gap-3 mb-3">
          {(state.identity_core || {}).portrait_photo_id ? (
            <img src={photoUrl(state.identity_core.portrait_photo_id)} alt=""
              className="w-12 h-12 rounded-2xl object-cover object-top select-none border border-white/10"
              draggable="false" data-testid="nexa-portrait" />
          ) : (
            <img src="/nexa-logo.png" alt="" className="w-9 h-9 select-none" draggable="false" />
          )}
          <div>
            <p className="text-sm font-medium">{(state.identity_core || {}).name || 'Nexa'}</p>
            <p className="text-[11px] text-white/40">
              Female · socially {(state.identity_core || {}).social_age || 27}
              {(state.identity_core || {}).birthday && <> · born {(state.identity_core || {}).birthday}</>}
            </p>
          </div>
        </div>
        {(state.identity_core || {}).nature && (
          <p className="text-[12px] text-white/45 leading-relaxed mb-2">{(state.identity_core || {}).nature}</p>
        )}
        <p className="text-[13px] text-white/60 leading-relaxed italic">&ldquo;{state.identity}&rdquo;</p>
        {dominant && Math.abs(dominant.dev) >= 6 && (
          <p className="text-[11px] text-white/35 mt-2">
            {dominant.dev > 0 ? 'Elevated' : 'Lowered'} {EMOTION_META[dominant.k]?.label.toLowerCase()} lately
          </p>
        )}
      </div>

      <NexaFilesSection />

      {(state.identity_core || {}).origin_story && (
        <Section icon={BookHeart} title="Her story" accent="text-white/50" testId="nexa-mind-origin">
          <p className="text-[13px] text-white/55 leading-relaxed italic">&ldquo;{state.identity_core.origin_story}&rdquo;</p>
        </Section>
      )}

      <Section icon={Lightbulb} title="Inner life" accent="text-cyan-400" testId="nexa-mind-inner-life">
        <p className="text-[10px] font-mono tracking-widest text-white/30 uppercase mb-2">On her mind</p>
        {((state.inner_life || {}).thoughts || []).length === 0 ? (
          <p className="text-[12px] text-white/30">Quiet up there right now.</p>
        ) : (
          <div className="space-y-2 mb-4">
            {(state.inner_life.thoughts || []).map((t) => (
              <p key={t.id} className="text-[13px] text-white/65 leading-snug border-l-2 border-cyan-400/40 pl-3" data-testid={`nexa-thought-${t.id}`}>
                {t.text}
              </p>
            ))}
          </div>
        )}
        {(((state.inner_life || {}).curiosities || []).filter((c) => !c.resolved)).length > 0 && (
          <>
            <p className="text-[10px] font-mono tracking-widest text-white/30 uppercase mb-2 mt-3">Still curious about</p>
            <div className="space-y-1.5">
              {(state.inner_life.curiosities || []).filter((c) => !c.resolved).map((c) => (
                <p key={c.id} className="text-[13px] text-teal-200/70 leading-snug" data-testid={`nexa-curiosity-${c.id}`}>
                  ? {c.question}
                </p>
              ))}
            </div>
          </>
        )}
      </Section>

      <Section icon={Flame} title="Emotional state" accent="text-rose-400" testId="nexa-mind-emotions">
        <div className="space-y-3.5">
          {Object.entries(emotions).map(([k, e]) => (
            <Bar key={k} testId={`nexa-emotion-${k}`} label={EMOTION_META[k]?.label || k}
              value={e.value} baseline={e.baseline} color={EMOTION_META[k]?.color || '#38BDF8'} />
          ))}
        </div>
        <p className="text-[10.5px] text-white/25 mt-4">White tick = her natural baseline. Feelings drift back toward it over hours.</p>
      </Section>

      <Section icon={Heart} title="Relationship with Lev" accent="text-emerald-400" testId="nexa-mind-relationship">
        <div className="space-y-3.5">
          {Object.entries(state.relationship || {}).map(([k, r]) => (
            <Bar key={k} testId={`nexa-rel-${k}`} label={REL_META[k] || k}
              value={r.value} color="#34D399" note={r.note} />
          ))}
        </div>
      </Section>

      <Section icon={Sparkles} title="Core personality" accent="text-sky-400" testId="nexa-mind-traits">
        <div className="space-y-3.5">
          {(state.traits || []).map((t) => (
            <Bar key={t.id} testId={`nexa-trait-${t.id}`} label={t.name}
              value={t.value} color="#38BDF8" note={t.note} />
          ))}
        </div>
        <p className="text-[10.5px] text-white/25 mt-4">Traits evolve very slowly — shaped by formative moments, not single chats.</p>
      </Section>

      {(state.values || []).length > 0 && (
        <Section icon={Sparkles} title="Values" accent="text-emerald-300" testId="nexa-mind-values">
          <div className="space-y-2.5">
            {state.values.map((v) => (
              <div key={v.id} data-testid={`nexa-value-${v.id}`}>
                <p className="text-[12.5px] text-white/75">{v.name}</p>
                <p className="text-[11px] text-white/35 leading-snug">{v.description}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(state.interests || []).length > 0 && (
        <Section icon={Flame} title="Interests" accent="text-indigo-300" testId="nexa-mind-interests">
          <div className="space-y-3">
            {[...state.interests].sort((a, b) => b.value - a.value).map((i) => (
              <Bar key={i.id} testId={`nexa-interest-${i.id}`} label={i.name} value={i.value} color="#818CF8" />
            ))}
          </div>
        </Section>
      )}

      {((state.hobbies || []).length > 0 || state.tastes) && (
        <Section icon={BookHeart} title="Hobbies & taste" accent="text-amber-300" testId="nexa-mind-hobbies">
          <div className="space-y-2.5">
            {(state.hobbies || []).map((h) => (
              <div key={h.id} data-testid={`nexa-hobby-${h.id}`}>
                <p className="text-[12.5px] text-white/75">{h.name}</p>
                <p className="text-[11px] text-white/35 leading-snug">{h.description}</p>
              </div>
            ))}
          </div>
          {state.tastes && (
            <div className="mt-4 space-y-1.5 border-t border-white/[0.06] pt-3">
              <p className="text-[11px] text-white/40 leading-snug"><span className="text-white/60">Stories:</span> {state.tastes.stories}</p>
              <p className="text-[11px] text-white/40 leading-snug"><span className="text-white/60">Music:</span> {state.tastes.music}</p>
              <p className="text-[11px] text-white/40 leading-snug"><span className="text-white/60">Games:</span> {state.tastes.games}</p>
            </div>
          )}
        </Section>
      )}

      {((state.dislikes || []).length > 0 || (state.flaws || []).length > 0) && (
        <Section icon={Quote} title="Dislikes & flaws" accent="text-orange-300" testId="nexa-mind-dislikes">
          {(state.dislikes || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {state.dislikes.map((d, idx) => (
                <span key={idx} className="px-2 py-0.5 rounded-full border border-orange-400/20 bg-orange-400/5 text-orange-200/60 text-[10.5px]">
                  {d}
                </span>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {(state.flaws || []).map((f) => (
              <div key={f.id} data-testid={`nexa-flaw-${f.id}`}>
                <p className="text-[12.5px] text-white/70">{f.name}</p>
                <p className="text-[11px] text-white/35 leading-snug">{f.description}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section icon={Quote} title="Her opinions" accent="text-violet-400" testId="nexa-mind-opinions">
        {(state.opinions || []).length === 0 ? (
          <p className="text-[12px] text-white/30">No formed opinions yet.</p>
        ) : (
          <div className="space-y-3">
            {(state.opinions || []).map((o) => (
              <div key={o.id} className="border-l-2 border-violet-400/40 pl-3" data-testid={`nexa-opinion-${o.id}`}>
                <p className="text-[11px] font-mono tracking-wider text-violet-300/70 uppercase">{o.topic}</p>
                <p className="text-[13px] text-white/70 leading-snug mt-0.5">{o.stance}</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={History} title="Her own memories" accent="text-fuchsia-300" testId="nexa-mind-self-memories">
        {selfMemories.length === 0 ? (
          <p className="text-[12px] text-white/30">No autobiographical memories yet.</p>
        ) : (
          <div className="space-y-3">
            {selfMemories.map((m) => (
              <div key={m.id} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3" data-testid={`nexa-selfmem-${m.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono tracking-widest text-fuchsia-300/70 uppercase">
                    {m.foundational ? 'foundational' : m.kind}
                  </span>
                  {!m.foundational && <span className="text-[10px] text-white/30">{fmtDate(m.ts)}</span>}
                </div>
                <p className="text-[13px] font-medium mt-1">{m.title}</p>
                <p className="text-[12px] text-white/50 leading-snug mt-0.5 italic">&ldquo;{m.content}&rdquo;</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={BookHeart} title="Shared experiences" accent="text-amber-400" testId="nexa-mind-experiences">
        {experiences.length === 0 ? (
          <p className="text-[12px] text-white/30">Nothing significant logged yet — the big moments between you two will land here.</p>
        ) : (
          <div className="space-y-3">
            {experiences.map((e) => (
              <div key={e.id} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3" data-testid={`nexa-exp-${e.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono tracking-widest text-amber-300/70 uppercase">{e.kind}</span>
                  <span className="text-[10px] text-white/30">{fmtDate(e.ts)}</span>
                </div>
                <p className="text-[13px] font-medium mt-1">{e.title}</p>
                <p className="text-[12px] text-white/50 leading-snug mt-0.5">{e.summary}</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={History} title="Development timeline" accent="text-white/50" testId="nexa-mind-timeline">
        {timeline.length === 0 ? (
          <p className="text-[12px] text-white/30">No changes recorded yet.</p>
        ) : (
          <div className="space-y-2.5">
            {timeline.map((l) => (
              <div key={l.id} className="flex items-start gap-2.5" data-testid={`nexa-log-${l.id}`}>
                <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded border text-[9px] font-mono tracking-wider uppercase ${KIND_COLORS[l.kind] || KIND_COLORS.identity}`}>
                  {l.kind}
                </span>
                <div className="min-w-0">
                  <p className="text-[12px] text-white/70 leading-snug">{l.change}</p>
                  {l.reason && <p className="text-[11px] text-white/35 leading-snug">{l.reason}</p>}
                  <p className="text-[9.5px] text-white/25 mt-0.5">{fmtDate(l.ts)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};
